import EmptyState from "../../components/shared/EmptyState";
import ErrorState from "../../components/shared/ErrorState";
import LoadingState from "../../components/shared/LoadingState";
import SchedulePositionRoundsTabsReadOnly from "../../components/schedule/SchedulePositionRoundsTabsReadOnly";
import ScheduleSectionCard from "../../components/schedule/ScheduleSectionCard";
import { SchedulePosition } from "../../interfaces/ISchedules";
import { usePublishedSchedules } from "../../hooks/usePublishedSchedules";
import { useNavigationHelper } from "../../hooks/useNavigationHelper";
import { useCompetitionsContext } from "../../contexts/CompetitionsContext";
import {
  getPositionLiveStateChipColor,
  getPositionLiveStateLabel,
} from "../../utils/schedulePositionStatusLabels";
import { Alert, Box, Chip, Container, Typography } from "@mui/material";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

const collectRoundItems = (position: SchedulePosition) =>
  position.items.filter(
    (item) => item.type === "ROUND" && item.competitionRoundId && item.competitionCategoryId,
  );

export default function SchedulePositionDetails() {
  const { competitionId, scheduleId, positionId } = useParams<{
    competitionId: string;
    scheduleId: string;
    positionId: string;
  }>();
  const { schedules, loading, error, refreshSchedules } = usePublishedSchedules(competitionId);
  const { goToScheduleDay } = useNavigationHelper();
  const { competitions } = useCompetitionsContext();

  const competitionName = useMemo(() => {
    if (!competitionId) {
      return undefined;
    }
    const competition = competitions.find((item) => item.id === competitionId);
    return competition?.fullName || competition?.name;
  }, [competitionId, competitions]);

  const schedule = schedules.find((item) => item.id === scheduleId);
  const position = schedule?.positions.find((p) => p.id === positionId);
  const dayLabel = schedule ? schedule.dayLabel || `Dzień ${schedule.day}` : undefined;

  const roundItems = position ? collectRoundItems(position) : [];

  if (!competitionId || !scheduleId || !positionId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <EmptyState message="Nieprawidłowy adres." />
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <LoadingState message="Ładowanie harmonogramu…" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ErrorState message={error} onRetry={refreshSchedules} />
      </Container>
    );
  }

  if (!schedule || !position) {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <ScheduleSectionCard
          title="Pozycja harmonogramu"
          subtitle={dayLabel ?? "Harmonogram"}
          tertiary={competitionName}
          onBack={() => goToScheduleDay(competitionId, scheduleId)}
        >
          <Typography variant="body2" color="text.secondary">
            Nie znaleziono tej pozycji lub harmonogram nie jest już opublikowany.
          </Typography>
        </ScheduleSectionCard>
      </Container>
    );
  }

  const statusChip = (
    <Chip
      size="small"
      label={getPositionLiveStateLabel(position.liveState)}
      color={getPositionLiveStateChipColor(position.liveState)}
      variant={position.liveState === "PLANNED" || !position.liveState ? "outlined" : "filled"}
    />
  );

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <ScheduleSectionCard
        title="Pozycja harmonogramu"
        subtitle={dayLabel ? `${dayLabel}` : undefined}
        tertiary={competitionName}
        afterTertiary={statusChip}
        onBack={() => goToScheduleDay(competitionId, scheduleId)}
        contentSx={{ px: 0, pt: 0, pb: { xs: 2, sm: 3 } }}
      >
        {roundItems.length === 0 ? (
          <Box sx={{ px: 2, pt: 2 }}>
            <Alert severity="info">
              Ta pozycja nie zawiera rund (typu ROUND) z podpiętą kategorią i rundą — brak listy prezentacji.
            </Alert>
          </Box>
        ) : (
          <SchedulePositionRoundsTabsReadOnly roundItems={roundItems} competitionId={competitionId} />
        )}
      </ScheduleSectionCard>
    </Container>
  );
}
